import {
  addDays,
  isValidRange,
  presetRange,
  type DateRange,
  type DrillFilter,
  type LocalDate,
  type RangePreset,
  type SpendDimension,
  type WeekStart,
} from '@tahi/core';

export type RangeChoice = RangePreset | 'custom';

const DIMS: readonly SpendDimension[] = [
  'category',
  'day',
  'week',
  'month',
  'weekday',
  'source',
  'method',
  'note',
];
const CHOICES: readonly RangeChoice[] = ['week', 'month', 'quarter', 'year', 'custom'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function defaultListRange(today: LocalDate): DateRange {
  return { start: addDays(today, -59), end: today };
}

export function parseDrill(
  sp: URLSearchParams,
  today: LocalDate,
  weekStartsOn: WeekStart,
): { filter: DrillFilter; isDrill: boolean } {
  const r = { start: sp.get('start') ?? '', end: sp.get('end') ?? '' };
  const range = isValidRange(r) ? r : defaultListRange(today);
  const dim = sp.get('dim');
  const key = sp.get('key');
  if (dim !== null && key !== null && (DIMS as readonly string[]).includes(dim)) {
    return { isDrill: true, filter: { range, dim: dim as SpendDimension, key, weekStartsOn } };
  }
  return { isDrill: false, filter: { range, weekStartsOn } };
}

export function drillHref(f: DrillFilter): string {
  const p = new URLSearchParams({ start: f.range.start, end: f.range.end });
  if (f.dim && f.key !== undefined) {
    p.set('dim', f.dim);
    p.set('key', f.key);
  }
  return `/m/spending/entries?${p.toString()}`;
}

export function parseRangeParams(
  sp: URLSearchParams,
  today: LocalDate,
  weekStartsOn: WeekStart,
): { preset: RangeChoice; range: DateRange } {
  const raw = sp.get('preset') ?? '';
  const preset: RangeChoice = (CHOICES as readonly string[]).includes(raw)
    ? (raw as RangeChoice)
    : 'month';
  if (preset === 'custom') {
    const r = { start: sp.get('start') ?? '', end: sp.get('end') ?? '' };
    return isValidRange(r)
      ? { preset, range: r }
      : { preset: 'month', range: presetRange('month', today) };
  }
  const anchor = sp.get('anchor');
  return {
    preset,
    range: presetRange(preset, anchor && DATE_RE.test(anchor) ? anchor : today, weekStartsOn),
  };
}

export function rangeSearch(preset: RangeChoice, range: DateRange): string {
  return preset === 'custom'
    ? `?preset=custom&start=${range.start}&end=${range.end}`
    : `?preset=${preset}&anchor=${range.start}`;
}
